/**
 * 선택한 로그 항목의 상세(§8.1). 값은 이미 마스킹된 것만 온다. JSON은 텍스트로만 보여 준다(ADV-G02).
 */
import { Caption1, makeStyles, tokens } from "@fluentui/react-components";
import { Checkmark16Regular, Copy16Regular, Dismiss16Regular } from "@fluentui/react-icons";
import { useEffect, useState } from "react";
import { FuiButton } from "@/components/fui-control";
import { SectionTitle } from "@/components/ui-typography";
import { formatDate } from "@/lib/date-format";
import { KIND_LABEL, LEVEL_LABEL, STATUS_LABEL, SURFACE_LABEL, type LogEntry } from "./log-model";

const useStyles = makeStyles({
  panel: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    flexShrink: 0,
    height: "240px",
    minHeight: 0,
    padding: tokens.spacingHorizontalM,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
  },
  head: { display: "flex", alignItems: "center", gap: tokens.spacingHorizontalS },
  title: { flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  body: { display: "grid", gridTemplateColumns: "minmax(260px, 1fr) 2fr", gap: tokens.spacingHorizontalL, flex: 1, minHeight: 0 },
  fields: { display: "grid", gridTemplateColumns: "88px 1fr", rowGap: "4px", columnGap: tokens.spacingHorizontalS, alignContent: "start", overflow: "auto" },
  key: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase300 },
  value: { fontSize: tokens.fontSizeBase300, minWidth: 0, overflowWrap: "anywhere", fontVariantNumeric: "tabular-nums" },
  json: {
    margin: 0,
    padding: tokens.spacingHorizontalS,
    overflow: "auto",
    minHeight: 0,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase300,
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
    color: tokens.colorNeutralForeground1,
  },
  copied: { color: tokens.colorNeutralForeground3 },
});

function metaValue(v: unknown): string {
  if (v == null) return "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

export function LogDetail({ entry, onClose }: { entry: LogEntry; onClose: () => void }) {
  const styles = useStyles();
  const [copied, setCopied] = useState(false);
  useEffect(() => setCopied(false), [entry.id]);
  const json = JSON.stringify(entry, null, 2);
  const meta = entry.meta ?? {};
  const rows: Array<[string, string]> = [
    ["시각", formatDate(entry.ts, "logTime")],
    ["ID", entry.id],
    ["요청 ID", metaValue(meta.requestId)],
    ["수준 · 상태", `${LEVEL_LABEL[entry.level]} · ${STATUS_LABEL[entry.status]}`],
    ["종류 · 경로", `${KIND_LABEL[entry.type]} · ${SURFACE_LABEL[entry.surface]}`],
    ["상대", entry.peer ?? "—"],
    ["인자", meta.argBytes == null ? "—" : `${metaValue(meta.argBytes)} B · ${metaValue(meta.argsHash)}`],
    ["결과", meta.resultBytes == null ? "—" : `${metaValue(meta.resultBytes)} B${meta.truncated === true ? " · 잘림" : ""}`],
    ["오류 코드", metaValue(meta.errorCode)],
  ];
  if (entry.source) rows.push(["출처", entry.source === "spool" ? "앱이 꺼져 있을 때 남긴 기록" : entry.source]);

  return (
    <section className={styles.panel} aria-label="로그 상세">
      <div className={styles.head}>
        <SectionTitle className={styles.title}>{entry.summary || entry.action}</SectionTitle>
        {copied && (
          <Caption1 className={styles.copied} role="status">
            복사했어요
          </Caption1>
        )}
        <FuiButton
          appearance="subtle"
          icon={copied ? <Checkmark16Regular /> : <Copy16Regular />}
          onClick={() => {
            void navigator.clipboard?.writeText(json).then(() => setCopied(true), () => setCopied(false));
          }}
        >
          원문 복사
        </FuiButton>
        <FuiButton appearance="subtle" icon={<Dismiss16Regular />} aria-label="상세 닫기" onClick={onClose} />
      </div>
      <div className={styles.body}>
        <div className={styles.fields}>
          {rows.map(([k, v]) => (
            <div key={k} style={{ display: "contents" }}>
              <span className={styles.key}>{k}</span>
              <span className={styles.value}>{v}</span>
            </div>
          ))}
        </div>
        <pre className={styles.json} aria-label="원문 JSON">
          {json}
        </pre>
      </div>
    </section>
  );
}
